'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LogoMarquee from './components/LogoMarquee';
import SearchBar from './components/SearchBar';
import JobGrid from './components/JobGrid';
import CounterStrip from './components/CounterStrip';
import Testimonials from './components/Testimonials';
import LeadCapture from './components/LeadCapture';
import StickyBar from './components/StickyBar';

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDepartment, setSearchDepartment] = useState('All Departments');
  const [searchTimezone, setSearchTimezone] = useState('Any Timezone');

  const handleSearch = (query: string, department: string, timezone: string) => {
    setSearchQuery(query);
    setSearchDepartment(department);
    setSearchTimezone(timezone);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero / Marquee Band */}
      <section id="companies" className="bg-gray-50 border-b border-fog-gray pt-12 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          {/* Live count line */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="w-2 h-2 rounded-full bg-signal-blue live-dot inline-block" />
            <p className="text-sm font-medium text-slate-pencil">
              <span className="text-signal-blue font-bold tabular-nums">1,247</span> remote roles live right now
            </p>
          </div>

          {/* Logo Marquee */}
          <LogoMarquee />

          {/* Search */}
          <div className="mt-10 max-w-3xl mx-auto">
            <SearchBar onSearch={handleSearch} />
          </div>

          {/* Track Equipment Shipping CTA */}
          <div className="mt-6 flex justify-center">
            <a
              href="https://www.ups.com/track"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-signal-blue text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
              Track Equipment Shipping
            </a>
          </div>
        </div>
      </section>

      {/* Job Grid */}
      <section id="jobs" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8 reveal-up visible">
            <div>
              <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-2">Open Roles</p>
              <h2 className="text-2xl font-semibold text-slate-dark tracking-tight">
                {searchQuery
                  ? `Results for "${searchQuery}"`
                  : 'Senior positions, distributed-first companies'}
              </h2>
            </div>
            <a href="#" className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-signal-blue hover:text-blue-700 transition-colors">
              View all 1,247 roles
              <span>→</span>
            </a>
          </div>
          <JobGrid
            searchQuery={searchQuery}
            searchDepartment={searchDepartment}
            searchTimezone={searchTimezone}
          />
        </div>
      </section>

      {/* Counter Strip */}
      <CounterStrip />

      {/* How It Works */}
      <section id="how-it-works" className="bg-white py-20 border-t border-fog-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-dark tracking-tight">How It Works</h2>
            <p className="mt-3 text-base text-slate-pencil max-w-xl mx-auto">Land your next remote role in three simple steps.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                step: '01',
                title: 'Browse Remote Roles',
                description: 'Explore hundreds of senior remote positions from distributed-first companies around the world.',
              },
              {
                step: '02',
                title: 'Apply in One Click',
                description: 'Submit your application directly through our platform — no account required to get started.',
              },
              {
                step: '03',
                title: 'Get Hired Remotely',
                description: 'Hear back from top companies and start your next remote chapter with confidence.',
              },
            ].map(({ step, title, description }) => (
              <div key={step} className="flex flex-col items-start gap-4 p-8 rounded-2xl border border-fog-gray bg-gray-50 hover:border-signal-blue/30 hover:shadow-sm transition-all">
                <span className="text-4xl font-black text-signal-blue/20 leading-none">{step}</span>
                <h3 className="text-lg font-semibold text-slate-dark">{title}</h3>
                <p className="text-sm text-slate-pencil leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="about-us" className="bg-white py-20 border-t border-fog-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-3">About</p>
            <h2 className="text-3xl font-bold text-slate-dark tracking-tight">About Us</h2>
            <p className="mt-3 text-base text-slate-pencil max-w-xl mx-auto">
              We are a community-driven platform that connects top remote roles with the best talent.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="p-8 rounded-2xl border border-fog-gray bg-gray-50 hover:border-signal-blue/30 hover:shadow-sm transition-all">
              <h3 className="text-lg font-semibold text-slate-dark">For Job Seekers</h3>
              <p className="text-sm text-slate-pencil leading-relaxed">
                Find your next remote role, submit your application, and get hired with confidence.
              </p>
            </div>
            <div className="p-8 rounded-2xl border border-fog-gray bg-gray-50 hover:border-signal-blue/30 hover:shadow-sm transition-all">
              <h3 className="text-lg font-semibold text-slate-dark">For Companies</h3>
              <p className="text-sm text-slate-pencil leading-relaxed">
                Post your remote roles, attract top talent, and hire with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="about" className="bg-gray-50 py-24 border-t border-fog-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — bold statement */}
            <div>
              <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-4">About Us</p>
              <h2 className="text-4xl font-black text-slate-dark tracking-tight leading-tight mb-6">
                Remote work is the future.<br />
                <span className="text-signal-blue">We make it yours.</span>
              </h2>
              <p className="text-base text-slate-pencil leading-relaxed mb-6">
                remotejobehire was built with one belief: the best talent shouldn't be limited by geography, and the best companies shouldn't be limited by zip codes. We bridge that gap — every single day.
              </p>
              <p className="text-base text-slate-pencil leading-relaxed">
                Whether you're a developer in Nairobi, a designer in Lisbon, or a marketer in Manila — your next opportunity is here. And if you're a company building a distributed team, your next great hire is too.
              </p>
            </div>

            {/* Right — two stacked cards */}
            <div className="flex flex-col gap-5">
              {/* Job Seekers card */}
              <div className="bg-white rounded-2xl border border-fog-gray p-8 hover:border-signal-blue/40 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-signal-blue/10 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-signal-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-dark mb-2">For Job Seekers</h3>
                    <p className="text-sm text-slate-pencil leading-relaxed">
                      Stop scrolling through noise. We surface hand-picked remote roles from companies that actually hire remotely — so you spend less time searching and more time interviewing. One platform, hundreds of real opportunities.
                    </p>
                  </div>
                </div>
              </div>

              {/* Companies card */}
              <div className="bg-white rounded-2xl border border-fog-gray p-8 hover:border-signal-blue/40 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-signal-blue/10 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-signal-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-dark mb-2">For Companies</h3>
                    <p className="text-sm text-slate-pencil leading-relaxed">
                      Post your remote roles and get in front of a focused, motivated talent pool — people who chose remote work intentionally. No cold outreach, no wasted interviews. Just qualified candidates ready to contribute from day one.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Lead Capture */}
      <LeadCapture />

      <Footer />

      {/* Sticky CTA Bar */}
      <StickyBar />
    </div>
  );
}